/**
 * Etapa 8 (visual etapa 4) — Revisao e ativacao.
 *
 * Tela de resumo: o gestor revisa tudo o que configurou antes de publicar.
 * Botoes finais ficam no rodape do wizard (Voltar / Salvar e publicar).
 */

import { useTranslation } from "react-i18next";
import type { PlanFormState } from "../types";

interface Props {
  state: PlanFormState;
}

export function Step8Review({ state }: Props) {
  const { t } = useTranslation();
  const cfg = state.onboardingConfig;

  const enabledSections: string[] = [];
  if (cfg.dadosPessoais.habilitado)
    enabledSections.push(t("charge_plans.step1_onboarding.card_pessoais.title"));
  if (cfg.contatos.habilitado)
    enabledSections.push(t("charge_plans.step1_onboarding.card_contatos.title"));
  if (cfg.referencias.habilitado)
    enabledSections.push(
      `${t("charge_plans.step1_onboarding.card_refs.title")} (${cfg.referencias.quantidade})`,
    );
  if (cfg.documentos.habilitado)
    enabledSections.push(
      `${t("charge_plans.step1_onboarding.card_docs.title")} (${cfg.documentos.lista.length})`,
    );
  if (cfg.fotosVideos.habilitado)
    enabledSections.push(
      `${t("charge_plans.step1_onboarding.card_media.title")} (${cfg.fotosVideos.lista.length})`,
    );
  if (cfg.chavePix.habilitado)
    enabledSections.push(t("charge_plans.step1_onboarding.card_pix.title"));

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          {t("charge_plans.step8_review.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("charge_plans.step8_review.subtitle")}
        </p>
      </header>

      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-bold text-foreground">
          {t("charge_plans.step8_review.section_general")}
        </h2>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t("charge_plans.step8_review.field_name")} value={state.nome || "—"} />
          <Field label={t("charge_plans.step8_review.field_code")} value={state.codigo || t("charge_plans.step8_review.code_autogen")} />
          <Field label={t("charge_plans.step8_review.field_value_mode")} value={state.tipoValor === "fixo" ? t("charge_plans.step8_review.value_fixed") : t("charge_plans.step8_review.value_client")} />
          <Field label={t("charge_plans.step8_review.field_max_active")} value={String(state.maxActiveCharges)} />
        </dl>
      </section>

      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-bold text-foreground">
          {t("charge_plans.step8_review.section_onboarding")}
        </h2>
        {enabledSections.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("charge_plans.step8_review.no_sections")}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {enabledSections.map((s) => (
              <li
                key={s}
                className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700"
              >
                ✓ {s}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-sm font-bold text-foreground">
          {t("charge_plans.step8_review.section_installments")}
        </h2>
        {state.installments.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("charge_plans.step8_review.no_installments")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm">
            {state.installments
              .filter((i) => i.ativo !== false)
              .map((i, idx) => (
                <li key={idx} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
                  <span className="font-medium text-foreground">
                    {i.label || i.tipo}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {i.numParcelas}x · {i.taxaPct}%
                  </span>
                </li>
              ))}
          </ul>
        )}
      </section>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <span className="font-semibold">⚠ </span>
        {t("charge_plans.step8_review.warning")}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}
